import { computed, ref, watch } from 'vue';
import {
  collection, query, onSnapshot, where,
} from 'firebase/firestore';
import { useWebNotification, useWindowFocus } from '@vueuse/core';
import { Store as $store } from '@store';
import { db } from '@boot/firebase';
import { api } from '@boot/axios';
import { getDefaultLocaleDatetimeString } from '@utils/dateFormat';
import { notificationTypes, notify } from '@utils/notifications';
import { getOperationName } from '@apollo/client/utilities';
import { currentOnCallAssignment } from '@graphql/onCallAssignments/currentOnCallAssignment';

const baseUrl = process.env.SERVER_URI;

export const CHAT_MESSAGE_TYPE = {
  FILE: 'file',
  FORM: 'form',
  TEXT: 'text',
  SYSTEM: 'system',
};

export const useChatParticipants = (chatRef) => {
  const loading = ref(false);
  const participants = computed(() => {
    const res = [];
    if (!chatRef.value?.participant_ids) {
      return res;
    }
    chatRef.value.participant_ids.forEach((pId) => {
      const found = $store.getters['chat/participantById'](pId);
      if (found) {
        res.push(found);
      }
    });

    return res;
  });

  const fetch = async (reload = false) => {
    if (reload) {
      $store.commit('chat/removeAllParticipants');
    }
    if (chatRef.value?.participant_ids) {
      await $store.dispatch('chat/fetchChatParticipants', chatRef.value.participant_ids);
    }
  };

  fetch();

  watch(chatRef, (newValue, oldValue) => {
    if (!oldValue || JSON.stringify(newValue.participant_ids) !== JSON.stringify(oldValue.participant_ids)) {
      fetch();
    }
  }, {
    deep: true,
  });

  return {
    fetch,
    participants,
    loading,
  };
};

export const getComputedCurrentUserId = () => computed(() => $store.getters['app/user']?.id);

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

export const createChatMessage = async (options) => await api.post(`${baseUrl}/api/chats/messages/create`, {
  form_id: options.form?.id,
  form_name: options.form?.name,
  message_type: options.message_type,
  message_text: options.message_text,
  chat_id: options.chat_id,
  file: options.file ? await toBase64(options.file) : undefined,
  file_name: options.file ? options.file.name : undefined,
});

export const markMessageAsRead = async (chatId, messageId) => {
  const res = await api.post(`${baseUrl}/api/chats/messages/read-by-user`, {
    chat_id: chatId,
    message_id: messageId,
  });

  return res;
};

export const editChatMessage = async (messageId, chatId, newMessage) => {
  const res = await api.post(`${baseUrl}/api/chats/messages/edit`, {
    chat_id: chatId,
    message_id: messageId,
    message_text: newMessage,
  });

  return res;
};

export const deleteChatMessage = async (messageId, chatId) => {
  const res = await api.post(`${baseUrl}/api/chats/messages/delete`, {
    chat_id: chatId,
    message_id: messageId,
  });

  if (res.data?.message_id) {
    return true;
  }

  return false;
};

export const chatTimeFormat = (dateString) => {
  // TODO: deal with TZ
  const date = new Date(dateString);
  const dateFormatted = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  const oneDayDiff = 86400000;
  const now = Date.now();
  if ((now - date) < oneDayDiff) {
    return `Today, ${dateFormatted}`;
  }
  if ((now - date) < oneDayDiff * 2) {
    return `Yesterday, ${dateFormatted}`;
  }

  return getDefaultLocaleDatetimeString(date);
};

const useNewMessageNotification = () => {
  const { isSupported, show: showWebNotification } = useWebNotification({ title: 'New chat message' });
  const windowFocused = useWindowFocus();

  const showNotification = (chat) => {
    const text = chat.last_message?.message_text;

    notify({
      type: notificationTypes.INFO,
      title: 'New chat message',
      message: `${text?.substr(0, 120)}…`,
      timeout: windowFocused.value ? 10000 : 0,
      actions: [{
        label: 'Open Chat',
        handler: () => {
          window.location = `/chats/${chat.id}`;
        },
      }],
    });

    if (!windowFocused.value && isSupported.value) {
      showWebNotification({ title: `New chat message: ${text}` });
    }
  };

  return { showNotification };
};

export const isUserCurrentOnCallAssigned = async () => {
  const currentUser = $store.getters['app/user'];

  const res = await api.graphQLRequest(
    getOperationName(currentOnCallAssignment),
    currentOnCallAssignment.loc.source.body,
  );

  const assignment = res.data?.data?.currentOnCallAssignment;

  return assignment && (
    +assignment.primary?.id === +currentUser.id
    || +assignment.secondary?.id === +currentUser.id
  );
};

export const useChatNotifications = async () => {
  const isLoggedIn = $store.getters['app/isLoggedIn'];
  const isAdminOrStaff = $store.getters['app/isAdminOrStaff'];

  if (!isLoggedIn) {
    return;
  }
  const currentUser = $store.getters['app/user'];

  await $store.dispatch('chat/firebaseSignIn');

  const { showNotification } = useNewMessageNotification();

  let q = query(
    collection(db, 'chats'),
    where('participant_ids', 'array-contains', currentUser.id),
  );

  // On-call assigned Staff/Admins should receive web-notification from all chats
  if (isAdminOrStaff) {
    q = query(collection(db, 'chats'));
  }

  let lastMessageId = null;
  onSnapshot(q, async (snapshot) => {
    const isCurrentOnCallAssigned = await isUserCurrentOnCallAssigned();

    snapshot.docChanges().forEach((change) => {
      const chat = { ...change.doc.data(), id: change.doc.id };
      const isChatParticipant = Array.isArray(chat.participant_ids)
        && chat.participant_ids.includes((pId) => +pId === +currentUser.id);
      if (change.type === 'modified') {
        if (
          lastMessageId !== chat.last_message?.id
          && +currentUser.id !== +chat.last_message?.user_id
          && (!isAdminOrStaff || (isAdminOrStaff && !isChatParticipant && isCurrentOnCallAssigned))
        ) {
          showNotification(chat);
        }
      }

      lastMessageId = chat.last_message?.id;
    });
  });
};
